import { useState, useEffect } from "react";
import { PROMISE_PENDING } from "./constants";
import BackToTop from "./components/buttons/backToTop/BackToTop";
import getWindow from "./utils/window";
import Prop65FullWarning from "./components/prop65/Prop65FullWarning";
import AdvertBanner from "./components/advertBanner/AdvertBanner";
import shouldShowProp65 from "./utils/shouldShowProp65";
import { useRuntimeConstants } from "./context/runtimeConstants/RuntimeConstantsContext";
import AppModals from "./containers/appModals/AppModals";
import AppNotificationBar from "./containers/appNotificationBar/AppNotificationBar";
import CarouselsAndReviews from "./containers/carouselsAndReviews/CarouselsAndReviews";
import ReactPortals from "./containers/reactPortals/ReactPortals";
import IfSingleProduct from "./components/conditional/IfSingleProduct";
import { checkForEdgeError } from "./utils/edgeError";
import { createAndInsertInToPage as createLegacyStructuredData } from "./utils/schemaOrg/schemaOrg";
import { createAndInsertInToPage as createStructuredDataV2 } from "./utils/structuredData/structuredData";
import useEventBus from "./hooks/useEventBus";
import { fireAddToBagMarketing } from "./utils/marketingTracking/marketingTrackingEvents";
import { updateDisplayedBreadcrumbs } from "./utils/displayBreadcrumbs";
import { useIsInStockContext } from "./context/isInStock/IsInStockContext";
import { useStockPrice } from "./context/stockPrice/StockPriceContext";
import ErrorBoundary from "./components/errorBoundary/ErrorBoundary";
import { useFeaturesReady } from "./context/featuresReady/FeaturesReadyContext";
import { trackCtaRefInOptimizely } from "./utils/trackCtaRefInOptimizely";

export default function App() {
  const {
    isMixAndMatch,
    product,
    breadcrumbsData,
    siteDomain,
    getTranslation,
    ratings,
    useStructuredDataV2,
  } = useRuntimeConstants();
  const { isInStock } = useIsInStockContext();
  const { products } = useStockPrice();
  const { areFeaturesReady } = useFeaturesReady();

  const [shouldShowProp65State, setShouldShowProp65State] =
    useState(PROMISE_PENDING);

  useEffect(() => {
    updateDisplayedBreadcrumbs({
      breadcrumbsData,
      siteDomain,
      getTranslation,
    });
    checkForEdgeError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (areFeaturesReady) {
      trackCtaRefInOptimizely();
    }
  }, [areFeaturesReady]);

  useEffect(() => {
    if (products) {
      if (useStructuredDataV2) {
        createStructuredDataV2({ products, ratings });
      } else {
        createLegacyStructuredData({ products, ratings });
      }
    }
  }, [products, ratings, useStructuredDataV2]);

  useEffect(() => {
    if (
      !isMixAndMatch &&
      shouldShowProp65State === PROMISE_PENDING &&
      isInStock !== null
    ) {
      shouldShowProp65({ product, isInStock })
        .then((prop65Shown) => {
          setShouldShowProp65State(prop65Shown);
          getWindow().asos.pdp["prop65Shown"] = prop65Shown;
          getWindow().asos.eventBus.emit("prop65Shown");
        })
        .catch(() => {
          setShouldShowProp65State(false);
          getWindow().asos.pdp["prop65Shown"] = false;
        });
    }
  }, [shouldShowProp65State, isMixAndMatch, product, isInStock]);

  useEventBus(
    "fireAddToBagMarketingEvent",
    (variantsAddedList) =>
      fireAddToBagMarketing({ products, variantsAddedList }),
    [products],
  );

  const renderProp65 = () =>
    shouldShowProp65State === true && <Prop65FullWarning />;

  return (
    <>
      {renderProp65()}
      <CarouselsAndReviews product={product} />
      <BackToTop />
      <IfSingleProduct>
        <ErrorBoundary>
          <AdvertBanner />
        </ErrorBoundary>
      </IfSingleProduct>
      <ReactPortals />
      <AppModals />
      <AppNotificationBar />
    </>
  );
}
